import React, { useRef, useEffect, useState } from "react";
import { Form, message, Image, Input, Button } from "antd";
import { YHDrawer } from "@/components/YhDrawer";
import { FormItemTypeEnum, IYhForm, IYhFormRef } from "@/components/YhFormBox/Types";
import { YhForm } from "@/components/YhFormBox";
import { getFormValidatorErrText, momentFormats } from "@/utils/utils";
import { useReportBillModel } from "../reportBillModel";
import useGlobalModel, { transOptions } from "@/model/globalModel";
import { reportBillService } from "../reportBillService";
import { getBaseImgLookUrl } from "@/utils/constants";
import { useDictModel } from '@/views/base/dict/dictModel';




// 进度节点配置
const getProgressNodes = (data: any) => {
    // 外修进度节点
    if (data.vrmExternalRepair) {
        return [
            {
                status: '已开单',
                key: 'create',
                timeField: 'createTime',
                userField: 'createUser'
            },
            {
                status: '待修',
                key: 'wait',
                timeField: 'endTime',
                userField: 'endUser'
            },
            {
                status: '在修',
                key: 'repairing',
                timeField: 'auditTime',
                userField: 'auditUser'
            },
            {
                status: '已完工',
                key: 'completed',
                timeField: 'checkTime',
                userField: 'checkUser'
            },
            {
                status: '已终检',
                key: 'completed',
                timeField: 'checkTime',
                userField: 'checkUser'
            }
        ];
    }
    // 内修进度节点
    return [
        {
            status: '已开单',
            key: 'create',
            timeField: 'createTime',
            userField: 'createUser'
        },
        {
            status: '待修',
            key: 'wait',
            timeField: 'tranTime',
            userField: 'tranUser'
        },
        {
            status: '在修',
            key: 'repairing',
            timeField: 'dispatchTime',
            userField: 'dispatcher'
        },
        {
            status: '已完工',
            key: 'completed',
            timeField: 'completeTime',
            userField: 'completeUser'
        },
        {
            status: '已终检',
            key: 'final',
            timeField: 'finalTime',
            userField: 'finalUser'
        }
    ];
};

export function ReportDrawer() {
    const { reportVisible: visible, setReportVisible, selectedRow } = useReportBillModel();
    const [detailData, setDetailData] = useState<any>({});
    const [isFaultExpand, setIsFaultExpand] = useState(false); // 故障描述展开状态
    const [isProgressExpand, setIsProgressExpand] = useState(false); // 进度展开状态
    const [approvalOpinion, setApprovalOpinion] = useState(''); // 审核意见
    const [submitting, setSubmitting] = useState(false); // 提交状态

    // 获取详情数据
    useEffect(() => {
        if (visible && selectedRow?.id) {
            fetchDetail(selectedRow.id);
        }
    }, [visible, selectedRow?.id]);
    const { getTreeNodeName, getTreeNodeOne } = useDictModel();


    // 获取详情
    const fetchDetail = async (id: string) => {
        try {
            const res = await reportBillService.one(id);
            let newName = ''
            if (res.retData.vrmExternalRepair) {
                newName =
                    (res.retData.vrmExternalRepair.status == 'external_repair.init' || res.retData.vrmExternalRepair.status == 'external_repair.send') ? '已开单' :
                        (res.retData.vrmExternalRepair.status == 'external_repair.sendend' || res.retData.vrmExternalRepair.status == 'external_repair.ing') ? '待修' :
                            res.retData.vrmExternalRepair.status == 'external_repair.audited' ? '在修' :
                                res.retData.vrmExternalRepair.status == 'external_repair.reject' ? '审批驳回' :
                                    res.retData.vrmExternalRepair.status == 'external_repair.complete' ? '已完工' :
                                        '已终检'
            } else if (res.retData.maintenance) {
                newName =
                    res.retData.maintenance.status == 'maintenance.create' ? '已开单' :
                        res.retData.maintenance.status == 'maintenance.await' ? '待修' :
                            (res.retData.maintenance.status == 'maintenance.underway' || res.retData.maintenance.status == 'maintenance.under-review' || res.retData.maintenance.status == 'maintenance.approved') ? '在修' :
                                res.retData.maintenance.status == 'maintenance.completed' ? '已完工' :
                                    (res.retData.maintenance.status == 'maintenance-status.final' || res.retData.maintenance.status == 'maintenance.settle') ? '已终检' :
                                        ''
            }
            setDetailData({
                ...res.retData,
                newName,
                createTime: momentFormats(res.retData.createTime),
                // 区分内修/外修
                repairType: res.retData.vrmExternalRepair ? '外修' : '内修',
                nowRealyStatus: res.retData.vrmExternalRepair ? getTreeNodeName(res.retData.vrmExternalRepair.status) : res.retData.maintenance ? getTreeNodeName(res.retData.maintenance.status) : '',
                // 费用信息
                repairCost: res.retData.vrmExternalRepair
                    ? res.retData.vrmExternalRepair.itemAmount
                    : res.retData.maintenance?.workingHoursAmount || '0.00',
                partsCost: res.retData.vrmExternalRepair
                    ? res.retData.vrmExternalRepair.materialAmount
                    : res.retData.maintenance?.materialScienceAmount || '0.00'
            });
            setApprovalOpinion(res.retData.approvalOpinion || '');
        } catch (error) {
            message.error('获取详情失败');
        } finally {
        }
    };

    // 计算总费用
    const calculateTotal = () => {
        const repairCost = Number(detailData.repairCost || 0);
        const partsCost = Number(detailData.partsCost || 0);
        return (repairCost + partsCost).toFixed(2);
    };


    // 获取进度列表
    const getProgressList = () => {
        const nodes = getProgressNodes(detailData);
        const targetData = detailData.vrmExternalRepair || detailData.maintenance || {};

        return nodes.map((node) => {
            const time = targetData[node.timeField] ? momentFormats(targetData[node.timeField]) : '';
            const user = targetData[node.userField] || '未知';
            // 判断节点是否激活（有时间即表示已完成）
            const active = !!targetData[node.timeField];
            return { ...node, time, user, active, expectedCompletionTime: targetData.expectedCompletionTime };
        });
    };


    const progressList = getProgressList();
    const isAudited = detailData.status === 'repair_report.audited' || detailData.status === 'repair_report.check';
    const canAudit = ['repair_report.init', 'repair_report.ing'].includes(detailData.status || '');

    return (
        <YHDrawer
            title="维修进度详情"
            visible={visible}
            cancelText="返回"
            onCancel={() => setReportVisible(false)}
            width={600}
        >
            <div style={{ padding: '0 24px' }}>
                {/* 顶部状态区域 */}
                <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    border: '1px solid #f0f0f0'
                }}>
                    <div style={{ backgroundColor: '#fafafa', padding: '12px', borderRadius: '4px' }}>
                        {progressList.find(item => item.active) ? (
                            <>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignContent: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    <div >
                                        <span style={{ color: '#000', fontSize: '18px' }}>当前状态：</span>
                                        <span style={{
                                            color: '#00BE87',
                                            fontSize: '18px',
                                            backgroundColor: 'rgba(0, 190, 135, 0.1)',
                                            padding: '2px 8px',
                                            borderRadius: '4px'
                                        }}>
                                            {detailData.newName}
                                        </span>
                                    </div>
                                    <div style={{ color: '#ffaa00' }}>
                                        ({detailData.repairType}){
                                            detailData.nowRealyStatus
                                        }
                                    </div>
                                </div>
                                <div>
                                    <span style={{ color: '#000', fontSize: '18px' }}>最新更新：</span>
                                    <span style={{ color: '#333', fontSize: '18px' }}>
                                        {progressList.find(item => item.status == detailData.newName)?.time || '--'}
                                    </span>
                                </div>
                                {momentFormats(progressList[0].expectedCompletionTime) && <div>
                                    <span style={{ color: '#000', fontSize: '18px' }}>预计完工时间：</span>
                                    <span style={{ color: '#333', fontSize: '18px' }}>
                                        {momentFormats(progressList[0].expectedCompletionTime)}
                                    </span>
                                </div>}
                            </>
                        ) : (
                            <div style={{ color: '#999', fontSize: '18px' }}>暂无进度信息</div>
                        )}
                    </div>
                </div>


                {/* 工单进度追踪 */}
                {isAudited && (
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        border: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ fontSize: '20px', margin: 0, color: '#333' }}>
                                工单进度追踪
                            </h3>
                        </div>

                        <div style={{ position: 'relative', paddingLeft: '24px' }}>
                            {progressList.map((item, index) => (
                                <div key={item.key} style={{ position: 'relative', marginBottom: '24px' }}>
                                    {/* 节点 */}
                                    <div style={{
                                        position: 'absolute',
                                        left: '-24px',
                                        top: '0',
                                        width: '16px',
                                        height: '16px',
                                        borderRadius: '50%',
                                        backgroundColor: item.active ? '#00BE87' : '#ccc'
                                    }}></div>

                                    {/* 内容 */}
                                    <div>
                                        <div style={{ fontSize: '18px', color: '#333', fontWeight: 'bold', marginBottom: '4px' }}>
                                            {item.status}
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#666', marginBottom: '2px' }}>
                                            操作人：{item.user}
                                        </div>
                                        <div style={{ fontSize: '16px', color: '#999' }}>
                                            时间：{item.time || '未完成'}
                                        </div>
                                    </div>

                                    {/* 连接线（最后一个节点没有） */}
                                    {index !== progressList.length - 1 && (
                                        <div style={{
                                            position: 'absolute',
                                            left: '-16px',
                                            top: '16px',
                                            width: '2px',
                                            height: 'calc(100% + 8px)',
                                            backgroundColor: '#eee'
                                        }}></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 费用信息 */}
                {isAudited && (
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '16px',
                        border: '1px solid #f0f0f0'
                    }}>
                        <h3 style={{ fontSize: '20px', marginBottom: '16px', color: '#333' }}>费用信息</h3>

                        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666', fontSize: '18px' }}>维修费用</span>
                            <span style={{ color: '#333', fontSize: '18px' }}>￥{detailData.repairCost}</span>
                        </div>

                        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#666', fontSize: '18px' }}>配件费用</span>
                            <span style={{ color: '#333', fontSize: '18px' }}>￥{detailData.partsCost}</span>
                        </div>

                        <div style={{ height: '1px', backgroundColor: '#f0f0f0', margin: '12px 0' }}></div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#333', fontSize: '20px', fontWeight: 'bold' }}>应付总金额</span>
                            <span style={{ color: '#FF6B6B', fontSize: '20px', fontWeight: 'bold' }}>￥{calculateTotal()}</span>
                        </div>
                    </div>
                )}

            </div>
        </YHDrawer>
    );
}